import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import GuessAddressesPanel from "@/components/GuessAddressesPanel";
import { toast } from "sonner";
import { AdminShell } from "@/components/Layout";
import { Broom, Trash, MapPin, Archive } from "@phosphor-icons/react";


// Quote-aware CSV record splitter → chunks small enough for the ingress body limit (~1MB).
function splitCsvRecords(text) {
  const recs = []; let cur = ""; let q = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (ch === '"') { q = !q; cur += ch; }
    else if ((ch === "\n" || ch === "\r") && !q) {
      if (ch === "\r" && text[i + 1] === "\n") i++;
      if (cur.trim()) recs.push(cur); cur = "";
    } else cur += ch;
  }
  if (cur.trim()) recs.push(cur);
  return recs;
}
function csvChunks(text, maxBytes = 400 * 1024) {
  const [header, ...rows] = splitCsvRecords(text);
  const chunks = []; let buf = [];
  let size = 0;
  for (const r of rows) {
    if (size + r.length > maxBytes && buf.length) { chunks.push([header, ...buf].join("\n")); buf = []; size = 0; }
    buf.push(r); size += r.length + 1;
  }
  if (buf.length) chunks.push([header, ...buf].join("\n"));
  return chunks;
}
async function postCsvChunked(path, text, dryRun, onProgress) {
  const chunks = csvChunks(text);
  const merged = { ok: true, dry_run: dryRun, stats: {}, sample: [], would_update: 0, created: 0, chunks: chunks.length };
  let matchedSum = 0, poolTotal = null;
  for (let i = 0; i < chunks.length; i++) {
    onProgress?.(i + 1, chunks.length);
    const r = (await api.post(path, { csv_text: chunks[i], dry_run: dryRun })).data;
    if (!r.ok) return r;
    for (const [k, v] of Object.entries(r.stats || {})) merged.stats[k] = (merged.stats[k] || 0) + (typeof v === "number" ? v : 0);
    if (r.stats?.matched_wos != null) { matchedSum += r.stats.matched_wos; poolTotal = r.stats.matched_wos + (r.stats.no_match_in_csv || 0); }
    merged.would_update += r.would_update || 0;
    merged.created += r.created || 0;
    merged.sample = merged.sample.concat(r.sample || []).slice(0, 10);
    merged.wo_col_used = merged.wo_col_used || r.wo_col_used;
    merged.addr_col_used = merged.addr_col_used || r.addr_col_used;
  }
  if (poolTotal != null) merged.stats.no_match_in_csv = Math.max(0, poolTotal - matchedSum);
  return merged;
}

export default function AdminCleanupAutoWos() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [newProperty, setNewProperty] = useState("");
  const [filter, setFilter] = useState("all"); // all | with_files | empty
  const [woBackfillScan, setWoBackfillScan] = useState(null);
  const [woBackfillBusy, setWoBackfillBusy] = useState(false);

  const runWoBackfillScan = async () => {
    setWoBackfillBusy(true);
    try {
      const r = await api.get("/jobs/backfill-wo-numbers/scan?limit=2000");
      setWoBackfillScan(r.data);
      if ((r.data.would_change_count || 0) === 0)
        toast.info("Scan complete — no recoverable WO#s found in title/description.");
      else
        toast.success(`Scan complete — ${r.data.would_change_count} WO#(s) recoverable. Review then Apply.`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Scan failed"));
    } finally { setWoBackfillBusy(false); }
  };

  const applyWoBackfill = async () => {
    if (!window.confirm(`Apply ${woBackfillScan?.would_change_count || 0} WO# rewrites? Placeholder PC-xxx values will be replaced with the recovered real WO#. Duplicates are skipped and flagged.`)) return;
    setWoBackfillBusy(true);
    try {
      const r = await api.post("/jobs/backfill-wo-numbers/apply?limit=2000");
      toast.success(`Updated ${r.data.updated_count} · Conflicts ${r.data.conflict_count} · No hit ${r.data.skipped_no_hit}`);
      setWoBackfillScan(null); // clear so user re-scans
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Apply failed"));
    } finally { setWoBackfillBusy(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/auto-created-wos");
      setData(r.data);
      setSelected(new Set());
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Load failed"));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = (data?.wos || []).filter(w => {
    if (filter === "with_files") return w.total_files > 0;
    if (filter === "empty") return w.total_files === 0;
    return true;
  });

  const toggle = (id) => {
    const s = new Set(selected);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelected(s);
  };
  const toggleAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map(w => w.id)));
  };

  const bulkArchive = async () => {
    if (selected.size === 0) return toast.error("Select some WOs first");
    if (!window.confirm(`Archive ${selected.size} WOs? They'll disappear from the active board.`)) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/auto-created-wos/archive",
        { ids: [...selected] });
      toast.success(`Archived ${r.data.archived}`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Archive failed"));
    } finally { setBusy(false); }
  };

  const bulkReassign = async () => {
    if (selected.size === 0) return toast.error("Select some WOs first");
    if (!newProperty.trim()) return toast.error("Enter a property address");
    if (!window.confirm(`Change property on ${selected.size} WOs to "${newProperty}"?`)) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/auto-created-wos/reassign-property",
        { ids: [...selected], property_address: newProperty.trim() });
      toast.success(`Updated ${r.data.updated}`);
      setNewProperty("");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Reassign failed"));
    } finally { setBusy(false); }
  };

  const deleteEmpty = async () => {
    setBusy(true);
    try {
      const dry = await api.post("/admin/auto-created-wos/delete-empty",
        { dry_run: true });
      const n = dry.data.would_delete;
      if (n === 0) { toast.info("No empty auto-WOs to delete"); return; }
      if (!window.confirm(`Delete ${n} empty auto-created WOs? This can't be undone.`)) return;
      const r = await api.post("/admin/auto-created-wos/delete-empty",
        { dry_run: false });
      toast.success(`Deleted ${r.data.deleted}`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Delete failed"));
    } finally { setBusy(false); }
  };

  const fixJobsBoard = async () => {
    if (!window.confirm("Fix Jobs Board? This turns the auto-WOs into real WOs in the Pool column with proper titles and cleared (bad) inherited addresses.")) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/auto-created-wos/fix-backlog-status");
      toast.success(`Enriched ${r.data.enriched} WOs · Jobs Board fixed`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Fix failed"));
    } finally { setBusy(false); }
  };

  const bulkMarkCompleted = async (all = false) => {
    if (!all && selected.size === 0) return toast.error("Select some WOs first");
    const label = all ? `ALL ${data?.wos.length || 0}` : selected.size;
    if (!window.confirm(`Mark ${label} auto-WOs as COMPLETED? They'll appear in the Completed bucket, files & titles preserved.`)) return;
    setBusy(true);
    try {
      const body = all ? { all: true } : { ids: [...selected] };
      const r = await api.post("/admin/auto-created-wos/mark-completed", body);
      toast.success(`Marked ${r.data.marked} as completed`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Mark completed failed"));
    } finally { setBusy(false); }
  };

  const [syncProgress, setSyncProgress] = useState(null);
  const syncFromClickUp = async () => {
    if (!window.confirm("Verify each auto-WO's status against its source ClickUp task? This will call ClickUp ~60 times per pass and auto-loop until every WO is synced (typically 6 passes for 352 WOs).")) return;
    setBusy(true);
    setSyncProgress({ done: 0, remaining: "…" });
    try {
      for (let pass = 0; pass < 12; pass++) {
        const r = await api.post("/admin/auto-created-wos/sync-status-from-clickup");
        const s = r.data?.stats || {};
        const n = (k) => Number(s[k] || 0);
        setSyncProgress({
          done: n("mapped_completed") + n("mapped_in_progress") + n("mapped_assigned") + n("mapped_pool"),
          remaining: r.data?.remaining_unsynced ?? "?",
          last: s,
        });
        toast.success(`Pass ${pass + 1}: ✔${n("mapped_completed")} ▶${n("mapped_in_progress")} ⚙${n("mapped_assigned")} ⏸${n("mapped_pool")}`);
        if ((r.data?.remaining_unsynced ?? 1) === 0) break;
      }
      await load();
      setSyncProgress(null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Sync failed"));
    } finally { setBusy(false); }
  };

  const [csvPreview, setCsvPreview] = useState(null);
  const [csvText, setCsvText] = useState("");
  const [progress, setProgress] = useState("");
  const onProg = (i, n) => setProgress(n > 1 ? `Uploading chunk ${i}/${n}…` : "");

  const onCsvFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    setCsvText(text);
    setCsvPreview(null);
    // Auto-dry-run
    setBusy(true);
    try {
      const d = await postCsvChunked("/admin/auto-created-wos/apply-csv", text, true, onProg);
      setCsvPreview(d);
      if (d.ok) {
        toast.success(`CSV parsed: ${d.stats.matched_wos} matches · ${d.would_update} to update`);
      } else {
        toast.error(d.error || "CSV parse issue — see preview");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? `CSV upload failed (${e.response?.status || e.message})`));
    } finally { setBusy(false); setProgress(""); }
  };

  const applyCsv = async () => {
    if (!csvText) return toast.error("Upload a CSV first");
    if (!window.confirm(`Apply ${csvPreview?.would_update || 0} address updates from the CSV?`)) return;
    setBusy(true);
    try {
      const d = await postCsvChunked("/admin/auto-created-wos/apply-csv", csvText, false, onProg);
      toast.success(`Updated ${d.stats.updated} addresses from AppFolio CSV`);
      setCsvPreview(null);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Apply failed"));
    } finally { setBusy(false); setProgress(""); }
  };

  const [histPreview, setHistPreview] = useState(null);
  const [histCount, setHistCount] = useState(null);

  const loadHistCount = async () => {
    try { const r = await api.get("/admin/auto-created-wos/history-count"); setHistCount(r.data); } catch {}
  };
  useEffect(() => { loadHistCount(); }, []);

  const previewHistory = async () => {
    if (!csvText) return toast.error("Upload a CSV first");
    setBusy(true);
    try {
      const d = await postCsvChunked("/admin/auto-created-wos/import-history", csvText, true, onProg);
      setHistPreview(d);
      if (d.ok) toast.success(`${d.stats.to_create} new WOs would be created`);
      else toast.error(d.error || "Parse issue");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Preview failed")); }
    finally { setBusy(false); setProgress(""); }
  };

  const applyHistory = async () => {
    const n = histPreview?.stats?.to_create || 0;
    if (!window.confirm(`Create ${n} work orders from the AppFolio CSV?\n\n${histPreview.stats.as_completed} as Completed (history) · ${histPreview.stats.as_pool} into the Pool.\n\nNo notifications or files are involved. Reversible via "Remove imported history".`)) return;
    setBusy(true);
    try {
      const d = await postCsvChunked("/admin/auto-created-wos/import-history", csvText, false, onProg);
      toast.success(`Created ${d.created} work orders from AppFolio history`);
      setHistPreview(null);
      await Promise.all([load(), loadHistCount()]);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Import failed")); }
    finally { setBusy(false); setProgress(""); }
  };

  const removeHistory = async () => {
    if (!window.confirm(`Delete all ${histCount?.total || 0} WOs imported from AppFolio CSV history? This only removes rows tagged appfolio_csv_history.`)) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/auto-created-wos/remove-history");
      toast.success(`Removed ${r.data.deleted} imported WOs`);
      await Promise.all([load(), loadHistCount()]);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Remove failed")); }
    finally { setBusy(false); }
  };

  const [cleanup, setCleanup] = useState(null);
  const [uuidFix, setUuidFix] = useState(null);
  const previewUuid = async () => {
    try { const r = await api.post("/admin/auto-created-wos/fix-uuid-addresses", { dry_run: true }); setUuidFix(r.data); }
    catch (e) { toast.error("Preview failed"); }
  };
  const applyUuid = async () => {
    if (!window.confirm(`Clear ${uuidFix.stats.jobs_with_uuid_address} UUID "addresses"? They become blank so Sync CU / CSV / Guess can fill the real street. The UUID is kept on the WO for reference.`)) return;
    try { const r = await api.post("/admin/auto-created-wos/fix-uuid-addresses", { dry_run: false }); toast.success(`Cleared ${r.data.cleared}`); setUuidFix(null); load(); }
    catch (e) { toast.error("Fix failed"); }
  };
  const previewCleanup = async () => {
    setBusy(true);
    try { const r = await api.post("/admin/auto-created-wos/finish-cleanup", { dry_run: true }); setCleanup(r.data); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Preview failed")); }
    finally { setBusy(false); }
  };
  const applyCleanup = async () => {
    const n = cleanup?.stats?.open_auto_wos || 0;
    if (!window.confirm(`Mark ${n} auto-created WOs as Completed (historical)?\n\nThey leave the Pool and Kanban but keep their files, titles and addresses. Anything ClickUp already marked done is untouched. No notifications.`)) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/auto-created-wos/finish-cleanup", { dry_run: false });
      toast.success(`Cleared ${r.data.updated} WOs out of the Pool`);
      setCleanup(null); await load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Cleanup failed")); }
    finally { setBusy(false); }
  };

  const s = data?.summary || {};

  return (
    <AdminShell>
      <div className="p-6 max-w-6xl mx-auto space-y-5">
        <div className="flex items-center gap-3">
          <Broom size={28} weight="fill" className="text-orange-600"/>
          <div>
            <h1 className="text-3xl font-black text-slate-900">Auto-Created WO Cleanup</h1>
            <p className="text-sm text-slate-600">Review the backlog WOs auto-created by the CASE_B backfill. Bulk-archive the ones you don't want, or reassign them to a real property.</p>
          </div>
        </div>
        <FwdAddressRepair/>

        {/* Recover real WO#s from title/description — dry-run then apply */}
        <div className="p-4 border-2 border-cyan-500 bg-cyan-50 rounded space-y-3" data-testid="wo-backfill-panel">
          <div className="flex items-center gap-2">
            <div className="font-black uppercase tracking-widest text-cyan-900 text-sm">🔍 Recover Real WO#s from Job Text</div>
          </div>
          <div className="text-xs text-slate-700">
            Scans every job whose WO# is empty or a <code className="px-1 bg-white border rounded">PC-xxx</code> placeholder,
            then pulls a real WO# (like <code>3363-1</code> or <code>AF-1234</code>) from the title / description
            when it's sitting right there in plain text. Dry-run first — nothing changes until you tap Apply.
          </div>
          <div className="flex gap-2 flex-wrap">
            <button onClick={runWoBackfillScan} disabled={woBackfillBusy}
              data-testid="wo-backfill-scan-btn"
              className="h-10 px-4 bg-cyan-600 hover:bg-cyan-700 disabled:opacity-40 text-white font-black uppercase text-xs tracking-widest rounded shadow-[2px_2px_0_#0f172a]">
              {woBackfillBusy ? "Scanning…" : "🔍 Scan (dry-run)"}
            </button>
            {woBackfillScan && (woBackfillScan.would_change_count || 0) > 0 && (
              <button onClick={applyWoBackfill} disabled={woBackfillBusy}
                data-testid="wo-backfill-apply-btn"
                className="h-10 px-4 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white font-black uppercase text-xs tracking-widest rounded shadow-[2px_2px_0_#0f172a]">
                ✅ Apply {woBackfillScan.would_change_count} rewrite{woBackfillScan.would_change_count > 1 ? "s" : ""}
              </button>
            )}
          </div>
          {woBackfillScan && (
            <div className="text-xs" data-testid="wo-backfill-result">
              <div className="text-slate-700 mb-2">
                Would change: <b>{woBackfillScan.would_change_count}</b> · No recoverable WO#: <b>{woBackfillScan.no_hit_count}</b>
              </div>
              {(woBackfillScan.would_change || []).length > 0 && (
                <div className="max-h-64 overflow-auto border rounded bg-white">
                  <table className="w-full text-[11px]">
                    <thead className="bg-slate-100 sticky top-0">
                      <tr><th className="p-1 text-left">Current</th><th className="p-1 text-left">→ New WO#</th><th className="p-1 text-left">Title</th><th className="p-1 text-left">Address</th></tr>
                    </thead>
                    <tbody>
                      {woBackfillScan.would_change.map(r => (
                        <tr key={r.job_id} className="border-t">
                          <td className="p-1 font-mono text-slate-500">{r.current_wo || "—"}</td>
                          <td className="p-1 font-mono font-black text-emerald-700">{r.recovered_wo}</td>
                          <td className="p-1">{r.title}</td>
                          <td className="p-1 text-slate-600">{r.address}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>

        {data && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatBox label="Total" value={s.total || 0} tone="slate"/>
            <StatBox label="With Files" value={s.with_files || 0} tone="emerald"/>
            <StatBox label="Empty" value={s.empty || 0} tone="orange"/>
            <StatBox label="Total Files" value={s.total_files || 0} tone="sky"/>
          </div>
        )}

        {/* One-tap Jobs Board fix — highlighted red so Andrew spots it on mobile */}
        <div className="p-4 border-2 border-red-500 bg-red-50 rounded space-y-2">
          <div className="font-black uppercase tracking-widest text-red-900 text-sm">⚠ Jobs Board broken? Tap here first.</div>
          <div className="text-xs text-slate-700">
            Turns the 352 auto-WOs into <b>real work orders in the Pool column</b> with proper ClickUp task titles, and clears the falsely-inherited property_address so you can see they need real addresses assigned.
          </div>
          <button onClick={fixJobsBoard} disabled={busy}
            data-testid="fix-jobs-board"
            className="h-12 px-6 bg-red-600 hover:bg-red-700 disabled:opacity-40 text-white font-black uppercase text-sm tracking-widest rounded shadow-[3px_3px_0_#0f172a] w-full sm:w-auto">
            {busy ? "Fixing…" : "🛠 Fix Jobs Board Now"}
          </button>
        </div>

        {/* Bulk sync — pull status + property address from source ClickUp task */}
        <div className="p-4 border-2 border-emerald-500 bg-emerald-50 rounded space-y-2">
          <div className="font-black uppercase tracking-widest text-emerald-900 text-sm">↻ Sync Status + Address from ClickUp</div>
          <div className="text-xs text-slate-700">
            For each auto-WO, fetch its source ClickUp task and mirror <b>real status</b> (completed / in_progress / assigned / pool) + <b>real property address</b> (from custom fields, description, or task name). Auto-loops until all synced (~6 passes, ~4 min).
          </div>
          <button onClick={syncFromClickUp} disabled={busy}
            data-testid="sync-from-clickup"
            className="h-12 px-6 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white font-black uppercase text-sm tracking-widest rounded shadow-[3px_3px_0_#0f172a] w-full sm:w-auto">
            {busy ? "Syncing…" : "↻ Sync from ClickUp"}
          </button>
          {syncProgress && (
            <div className="text-xs font-mono bg-emerald-100 border border-emerald-300 rounded px-3 py-2 mt-2">
              <b>Progress:</b> {syncProgress.done} done · {syncProgress.remaining} remaining
              {syncProgress.last && (
                <div className="mt-1 opacity-80">
                  ✔ {syncProgress.last.mapped_completed} completed ·
                  ▶ {syncProgress.last.mapped_in_progress} in-progress ·
                  ⚙ {syncProgress.last.mapped_assigned} assigned ·
                  ⏸ {syncProgress.last.mapped_pool} pool ·
                  📍 {syncProgress.last.address_found}/{syncProgress.last.fetched} addresses found
                </div>
              )}
            </div>
          )}
        </div>

        <div className="p-4 border-2 border-emerald-600 bg-emerald-50 rounded space-y-2" data-testid="finish-cleanup-panel">
          <div className="font-black uppercase tracking-widest text-emerald-900 text-sm">✅ Finish the 2548-1 cleanup</div>
          <div className="text-xs text-slate-700">
            Everything auto-created from the ClickUp backlog is historical. This moves every one still sitting in the Pool (or assigned) to <b>Completed</b>, dated from its own created date, with a system note. ClickUp-synced statuses and archived WOs are left alone. The 25 real AppFolio open WOs are not touched.
          </div>
          {!cleanup ? (
            <button onClick={previewCleanup} disabled={busy} data-testid="finish-cleanup-preview"
              className="h-10 px-4 bg-emerald-700 hover:bg-emerald-800 disabled:opacity-40 text-white font-black uppercase text-xs tracking-widest rounded shadow-[3px_3px_0_#0f172a] w-full">
              Preview what would change
            </button>
          ) : (
            <div className="space-y-2">
              <div className="text-xs font-mono bg-white border border-emerald-300 rounded p-2 grid grid-cols-2 gap-x-4 gap-y-0.5" data-testid="finish-cleanup-stats">
                <span>Still open (Pool etc.)</span><b data-testid="cleanup-open">{cleanup.stats.open_auto_wos}</b>
                <span className="pl-3">↳ without address</span><b>{cleanup.stats.without_address}</b>
                <span>Already completed</span><b>{cleanup.stats.already_completed}</b>
                <span>Archived</span><b>{cleanup.stats.archived}</b>
              </div>
              {cleanup.sample?.length > 0 && (
                <div className="text-[11px] font-mono bg-white border border-emerald-200 rounded max-h-32 overflow-auto">
                  {cleanup.sample.map((r, i) => <div key={i} className="px-2 py-1 border-b border-slate-100 truncate"><b>{r.wo}</b> · {r.status} · {r.address || <i className="text-slate-400">no address</i>} — {r.title}</div>)}
                </div>
              )}
              <div className="flex gap-2">
                <button onClick={() => setCleanup(null)} className="h-10 px-4 border-2 border-slate-300 text-xs font-black uppercase tracking-widest rounded">Cancel</button>
                <button onClick={applyCleanup} disabled={busy || cleanup.stats.open_auto_wos === 0} data-testid="finish-cleanup-apply"
                  className="flex-1 h-10 px-4 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white font-black uppercase text-xs tracking-widest rounded shadow-[3px_3px_0_#0f172a]">
                  Mark {cleanup.stats.open_auto_wos} Completed — clear the Pool
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="p-4 border-2 border-orange-500 bg-orange-50 rounded space-y-2" data-testid="uuid-fix-panel">
          <div className="font-black uppercase tracking-widest text-orange-900 text-sm">🧬 Fix UUID "addresses"</div>
          <div className="text-xs text-slate-700">Some WOs show a long code like <code>083b3a12-8beb-11ef-…</code> instead of a street. That's a ClickUp "Property" dropdown option ID the old sync stored by mistake. This clears them (UUID kept for reference) so <b>Sync CU</b>, the AppFolio CSV, or Guess Address can fill the real address. The sync itself is now fixed to read the dropdown's label.</div>
          {!uuidFix ? (
            <button onClick={previewUuid} className="h-10 px-4 bg-orange-600 hover:bg-orange-700 text-white font-black uppercase text-xs tracking-widest rounded shadow-[3px_3px_0_#0f172a] w-full" data-testid="uuid-fix-preview">Scan for UUID addresses</button>
          ) : (
            <div className="space-y-2">
              <div className="text-xs font-mono bg-white border border-orange-300 rounded p-2"><b data-testid="uuid-count">{uuidFix.stats.jobs_with_uuid_address}</b> WOs · {uuidFix.stats.distinct_uuids} distinct codes
                {uuidFix.stats.sample.map((r, i) => <div key={i} className="truncate text-[11px] text-slate-600">{r.wo} · {r.status} · {r.uuid}</div>)}</div>
              <div className="flex gap-2">
                <button onClick={() => setUuidFix(null)} className="h-10 px-4 border-2 border-slate-300 text-xs font-black uppercase tracking-widest rounded">Cancel</button>
                <button onClick={applyUuid} disabled={uuidFix.stats.jobs_with_uuid_address === 0} data-testid="uuid-fix-apply" className="flex-1 h-10 px-4 bg-orange-600 hover:bg-orange-700 disabled:opacity-40 text-white font-black uppercase text-xs tracking-widest rounded shadow-[3px_3px_0_#0f172a]">Clear {uuidFix.stats.jobs_with_uuid_address} UUID addresses</button>
              </div>
            </div>
          )}
        </div>

        <GuessAddressesPanel onApplied={load}/>

        {/* AppFolio CSV upload — bulk assign real addresses */}
        <div className="p-4 border-2 border-sky-500 bg-sky-50 rounded space-y-3">
          <div className="font-black uppercase tracking-widest text-sky-900 text-sm">📄 Bulk Address from AppFolio CSV</div>
          <div className="text-xs text-slate-700">
            <b>Step 1:</b> Download the skeleton CSV — it has every auto-WO# pre-filled with an empty <code>Property Address</code> column.<br/>
            <b>Step 2:</b> Fill in addresses (copy-paste from AppFolio, or export AF to a CSV with matching columns).<br/>
            <b>Step 3:</b> Upload — Field Radar auto-detects the WO# and Address columns, shows a preview, then applies on confirm.
          </div>
          <div className="flex flex-wrap gap-2">
            <a href="/api/admin/auto-created-wos/skeleton-csv"
               download="auto-wos-skeleton.csv"
               data-testid="download-skeleton"
               className="h-10 px-4 border-2 border-sky-600 bg-white text-sky-800 hover:bg-sky-100 font-black uppercase text-xs tracking-widest rounded inline-flex items-center">
              ⬇ Download Skeleton CSV
            </a>
            <label className="h-10 px-4 border-2 border-sky-600 bg-white text-sky-800 hover:bg-sky-100 font-black uppercase text-xs tracking-widest rounded inline-flex items-center cursor-pointer">
              📤 Upload Filled CSV
              <input type="file" accept=".csv,text/csv" onChange={onCsvFile}
                data-testid="upload-csv" className="hidden"/>
            </label>
          </div>
          {csvPreview && csvPreview.ok && (
            <div className="text-xs bg-sky-100 border border-sky-300 rounded p-3 space-y-2">
              <div className="font-mono">
                <b>Parsed:</b> {csvPreview.stats.csv_rows} CSV rows · <b>Matched:</b> {csvPreview.stats.matched_wos} auto-WOs · <b>Will update:</b> {csvPreview.would_update}
                <div className="opacity-75">
                  Column used → WO: <code>{csvPreview.wo_col_used}</code> · Address: <code>{csvPreview.addr_col_used}</code>
                </div>
              </div>
              {csvPreview.sample?.length > 0 && (
                <div className="max-h-40 overflow-y-auto border border-sky-200 bg-white rounded">
                  <table className="w-full text-[10px] font-mono">
                    <thead className="bg-sky-100"><tr>
                      <th className="p-1 text-left">WO</th>
                      <th className="p-1 text-left">Old</th>
                      <th className="p-1 text-left">→ New</th>
                    </tr></thead>
                    <tbody>
                      {csvPreview.sample.map((u, i) => (
                        <tr key={i} className="border-t border-sky-100">
                          <td className="p-1 font-bold">{u.wo}</td>
                          <td className="p-1 text-slate-500 truncate max-w-[100px]">{u.old || "—"}</td>
                          <td className="p-1 text-emerald-700 truncate max-w-[180px]">{u.new}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <button onClick={applyCsv} disabled={busy || csvPreview.would_update === 0}
                data-testid="apply-csv"
                className="h-10 px-4 bg-sky-600 hover:bg-sky-700 disabled:opacity-40 text-white font-black uppercase text-xs tracking-widest rounded shadow-[3px_3px_0_#0f172a] w-full">
                ✅ Apply {csvPreview.would_update} Address Updates
              </button>
            </div>
          )}
          {csvPreview && !csvPreview.ok && (
            <div className="text-xs bg-red-100 border border-red-300 rounded p-3 font-mono">
              <b>Error:</b> {csvPreview.error}<br/>
              <div className="opacity-75 mt-1">
                Detected headers: {(csvPreview.detected_headers || []).join(" · ")}<br/>
                Rename one column to <code>Work Order</code> and another to <code>Property Address</code> and re-upload.
              </div>
            </div>
          )}

          {progress && <div className="text-xs font-black uppercase tracking-widest text-sky-700 animate-pulse" data-testid="csv-progress">{progress}</div>}
          {csvText && (
            <div className="mt-3 border-t-2 border-dashed border-slate-300 pt-3 space-y-2" data-testid="history-import-section">
              <div className="text-xs font-black uppercase tracking-widest">Step 2 — Import remaining rows as work-order history</div>
              <div className="text-xs opacity-75">
                Every CSV row whose WO# isn't already in Field Radar becomes a WO. Completed / Work Done → <b>Completed</b> (history only, off the board). Open statuses → <b>Pool</b>. Dedupes by WO#. No notifications, no files.
              </div>
              {!histPreview && (
                <button onClick={previewHistory} disabled={busy} data-testid="preview-history"
                  className="h-10 px-4 bg-slate-800 hover:bg-slate-900 disabled:opacity-40 text-white font-black uppercase text-xs tracking-widest rounded shadow-[3px_3px_0_#0f172a] w-full">
                  🔍 Preview History Import
                </button>
              )}
              {histPreview?.ok && (
                <div className="space-y-2" data-testid="history-preview">
                  <div className="text-xs font-mono bg-white border border-slate-300 rounded p-2 grid grid-cols-2 gap-x-4 gap-y-0.5">
                    <span>CSV rows</span><b data-testid="hist-csv-rows">{histPreview.stats.csv_rows}</b>
                    <span>Already in Field Radar (skip)</span><b>{histPreview.stats.already_exists}</b>
                    <span>Duplicate WO# in CSV (skip)</span><b>{histPreview.stats.duplicate_in_csv}</b>
                    <span className="text-emerald-700">Will create</span><b className="text-emerald-700" data-testid="hist-to-create">{histPreview.stats.to_create}</b>
                    <span className="pl-3">↳ as Completed</span><b>{histPreview.stats.as_completed}</b>
                    <span className="pl-3">↳ into Pool</span><b>{histPreview.stats.as_pool}</b>
                    <span className="text-amber-700">Existing open WOs closed in AppFolio → mark Completed</span><b className="text-amber-700" data-testid="hist-to-mark-completed">{histPreview.stats.to_mark_completed ?? 0}</b>
                  </div>
                  {histPreview.sample?.length > 0 && (
                    <div className="text-[11px] font-mono bg-white border border-slate-300 rounded max-h-40 overflow-auto">
                      {histPreview.sample.map((r, i) => (
                        <div key={i} className="px-2 py-1 border-b border-slate-100 flex gap-2">
                          <span className="w-16 shrink-0 font-bold">{r.wo}</span>
                          <span className="w-20 shrink-0">{r.created_at}</span>
                          <span className={`w-20 shrink-0 ${r.status === "completed" ? "text-slate-500" : "text-sky-700 font-bold"}`}>{r.status}</span>
                          <span className="truncate">{r.address}{r.unit && r.unit !== "1" ? `, ${r.unit}` : ""} — {r.title}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <button onClick={applyHistory} disabled={busy || histPreview.stats.to_create === 0} data-testid="apply-history"
                    className="h-10 px-4 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white font-black uppercase text-xs tracking-widest rounded shadow-[3px_3px_0_#0f172a] w-full">
                    ✅ Create {histPreview.stats.to_create} Work Orders from History
                  </button>
                </div>
              )}
              {histPreview && !histPreview.ok && (
                <div className="text-xs bg-red-100 border border-red-300 rounded p-3 font-mono"><b>Error:</b> {histPreview.error}</div>
              )}
            </div>
          )}

          {histCount?.total > 0 && (
            <div className="mt-3 flex items-center justify-between gap-2 text-xs bg-amber-50 border border-amber-300 rounded p-2" data-testid="history-count">
              <span><b>{histCount.total}</b> WOs imported from AppFolio history ({histCount.completed} completed · {histCount.pool} pool)</span>
              <button onClick={removeHistory} disabled={busy} data-testid="remove-history"
                className="h-8 px-3 bg-red-600 hover:bg-red-700 disabled:opacity-40 text-white font-black uppercase text-[10px] tracking-widest rounded shrink-0">
                Remove imported history
              </button>
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-2 items-center pt-2">
          <select value={filter} onChange={e => setFilter(e.target.value)}
            data-testid="cleanup-filter"
            className="h-10 px-3 border-2 border-slate-300 rounded bg-white text-sm font-bold uppercase tracking-wide">
            <option value="all">All ({data?.wos.length || 0})</option>
            <option value="with_files">With Files</option>
            <option value="empty">Empty</option>
          </select>
          <button onClick={load} disabled={loading || busy}
            data-testid="cleanup-reload"
            className="h-10 px-4 border-2 border-slate-300 bg-white hover:bg-slate-50 font-black uppercase text-xs tracking-widest rounded">
            {loading ? "Loading…" : "↻ Reload"}
          </button>
          <div className="ml-auto text-xs text-slate-600 font-mono">Selected: <b>{selected.size}</b></div>
        </div>

        {/* Bulk actions */}
        <div className="p-4 border-2 border-slate-200 bg-slate-50 rounded space-y-3">
          <div className="font-black uppercase tracking-widest text-slate-700 text-xs">Bulk Actions</div>
          <div className="flex flex-wrap gap-2">
            <button onClick={bulkArchive} disabled={busy || selected.size === 0}
              data-testid="bulk-archive"
              className="h-10 px-4 border-2 border-orange-600 bg-white text-orange-800 hover:bg-orange-100 disabled:opacity-40 font-black uppercase text-xs tracking-widest rounded flex items-center gap-2">
              <Archive size={16} weight="bold"/> Archive Selected
            </button>
            <button onClick={deleteEmpty} disabled={busy}
              data-testid="delete-empty"
              className="h-10 px-4 border-2 border-red-600 bg-white text-red-800 hover:bg-red-50 font-black uppercase text-xs tracking-widest rounded flex items-center gap-2">
              <Trash size={16} weight="bold"/> Delete All Empty
            </button>
          </div>
          <div className="flex flex-wrap gap-2 items-center pt-2 border-t border-slate-200">
            <input list="known-addresses" autoComplete="off" type="text" value={newProperty} onChange={e => setNewProperty(e.target.value)}
              placeholder="Start typing an address… (e.g. 1834 W Ontario St)"
              data-testid="bulk-property-input"
              className="h-10 px-3 border-2 border-slate-300 rounded flex-1 min-w-[200px] text-sm"/>
            <button onClick={bulkReassign} disabled={busy || selected.size === 0 || !newProperty.trim()}
              data-testid="bulk-reassign"
              className="h-10 px-4 bg-sky-600 hover:bg-sky-700 disabled:opacity-40 text-white font-black uppercase text-xs tracking-widest rounded flex items-center gap-2 shadow-[3px_3px_0_#0f172a]">
              <MapPin size={16} weight="bold"/> Reassign Property
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="border-2 border-slate-200 rounded overflow-hidden">
          <table className="w-full text-sm" data-testid="cleanup-table">
            <thead className="bg-slate-100 text-xs uppercase tracking-widest">
              <tr>
                <th className="p-2 text-left w-10">
                  <input type="checkbox"
                    checked={filtered.length > 0 && selected.size === filtered.length}
                    onChange={toggleAll}
                    data-testid="toggle-all"/>
                </th>
                <th className="p-2 text-left">WO #</th>
                <th className="p-2 text-left">Status</th>
                <th className="p-2 text-left">Property</th>
                <th className="p-2 text-right">Photos</th>
                <th className="p-2 text-right">Notes</th>
                <th className="p-2 text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(w => (
                <tr key={w.id} className={selected.has(w.id) ? "bg-orange-50" : "hover:bg-slate-50"}>
                  <td className="p-2">
                    <input type="checkbox" checked={selected.has(w.id)}
                      onChange={() => toggle(w.id)}
                      data-testid={`row-checkbox-${w.work_order_number}`}/>
                  </td>
                  <td className="p-2 font-mono font-bold">{w.work_order_number}</td>
                  <td className="p-2 text-xs">{String(w.status ?? "")}</td>
                  <td className="p-2 text-xs text-slate-600 truncate max-w-[220px]">{typeof w.property_address === "string" ? w.property_address : JSON.stringify(w.property_address ?? "")}</td>
                  <td className="p-2 text-right font-mono">{w.photo_count}</td>
                  <td className="p-2 text-right font-mono">{w.clickup_note_count}</td>
                  <td className={"p-2 text-right font-mono font-black " +
                                  (w.total_files > 0 ? "text-emerald-700" : "text-slate-400")}>
                    {w.total_files}
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && !loading && (
                <tr><td colSpan={7} className="p-6 text-center text-sm text-slate-500">
                  No auto-created WOs match the current filter.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </AdminShell>
  );
}

function StatBox({ label, value, tone }) {
  const tones = {
    slate:   "bg-slate-100 text-slate-900 border-slate-300",
    emerald: "bg-emerald-50 text-emerald-900 border-emerald-300",
    orange:  "bg-orange-50 text-orange-900 border-orange-300",
    sky:     "bg-sky-50 text-sky-900 border-sky-300",
  };
  return (
    <div className={"border-2 rounded p-3 " + (tones[tone] || tones.slate)}>
      <div className="text-xs uppercase tracking-widest font-bold opacity-70">{label}</div>
      <div className="text-3xl font-black font-mono">{value}</div>
    </div>
  );
}

function FwdAddressRepair() {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async (dry) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/auto-created-wos/repair-fwd-addresses?dry_run=${dry}`);
      setPreview(data);
      if (!dry) toast.success(`Fixed ${data.count} forwarded WOs`);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Failed")); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border-2 border-amber-300 bg-amber-50 p-4" data-testid="fwd-address-repair">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] font-black uppercase tracking-widest text-amber-800">Forwarded-email fix</div>
          <div className="text-sm text-slate-700 mt-0.5">"Fwd:" WOs that got the signature (e.g. "Management Redefined, LLC") as their property. Re-reads address, unit and title from the subject line so Property History stops mixing them together.</div>
        </div>
        <div className="flex gap-2">
          <button onClick={() => run(true)} disabled={busy} data-testid="fwd-repair-preview" className="px-3 py-1.5 border-2 border-slate-900 bg-white text-xs font-black uppercase tracking-widest disabled:opacity-50">Preview</button>
          <button onClick={() => run(false)} disabled={busy || !preview?.count} data-testid="fwd-repair-apply" className="px-3 py-1.5 bg-slate-900 text-white text-xs font-black uppercase tracking-widest disabled:opacity-50">Fix {preview?.count ?? ""}</button>
        </div>
      </div>
      {preview && (
        <div className="mt-3 text-xs space-y-1 max-h-48 overflow-y-auto" data-testid="fwd-repair-list">
          {preview.count === 0 && <div className="text-slate-500">Nothing to fix.</div>}
          {preview.fixes.map(f => (
            <div key={f.id} className="flex gap-2 flex-wrap"><b>WO {f.wo}</b><span className="line-through text-slate-400">{f.from || "(blank)"}</span><span>→</span><span className="font-bold">{f.to}{f.unit ? ` · Unit ${f.unit}` : ""}</span></div>
          ))}
        </div>
      )}
    </div>
  );
}
